import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  ClipboardList, User, PencilLine, Building2, PhoneCall, UserPlus,
  LayoutDashboard, MessagesSquare, Star, ChevronLeft, ChevronRight,
} from "lucide-react";

const SLIDES = [
  {
    icon: ClipboardList,
    kicker: "Brief · Step 1",
    title: "Quick overview",
    body: "We kick off with a short recap of everything you'll fill out — so you know exactly what's coming. No surprises.",
  },
  {
    icon: User,
    kicker: "Brief · Step 2",
    title: "Your details",
    body: "Just the basics — your name, phone number and email — so we can reach you and set up your account.",
  },
  {
    icon: PencilLine,
    kicker: "Brief · Step 3",
    title: "What needs to be made",
    body: "Describe your project in your own words, and tell us the main colors or color scheme you have in mind.",
  },
  {
    icon: Building2,
    kicker: "Brief · Step 4",
    title: "Business, domain & logo",
    body: "Your business name, whether you already own a domain, and whether you have a logo ready to go.",
  },
  {
    icon: PhoneCall,
    kicker: "Brief · Step 5",
    title: "Request a call back",
    body: "Leave your number, pick a preferred date and time window, and we'll call you — no meetings to schedule.",
  },
  {
    icon: UserPlus,
    kicker: "Brief · Step 6",
    title: "Who referred you",
    body: "If someone sent you our way, drop their name. Then submit — and create your account to track everything.",
  },
  {
    icon: LayoutDashboard,
    kicker: "Then · Track",
    title: "Your own project dashboard",
    body: "A live progress tracker shows exactly what phase your build is in — from Discovery to Done.",
  },
  {
    icon: MessagesSquare,
    kicker: "Then · Talk",
    title: "Direct chat with photos & video",
    body: "Message us right inside your dashboard, share screenshots and clips, and get email notifications instantly.",
  },
  {
    icon: Star,
    kicker: "Finally · Own & review",
    title: "You own the code — then tell the world",
    body: "Get 100% of the source code when we're done, and leave a review that lands right on our homepage.",
  },
];

// direction-aware slide variants (reverse animation when going back)
const variants = {
  enter: (dir) => ({ opacity: 0, x: dir >= 0 ? 60 : -60 }),
  center: { opacity: 1, x: 0 },
  exit: (dir) => ({ opacity: 0, x: dir >= 0 ? -60 : 60 }),
};

export function FeatureSlides() {
  const [[i, dir], setState] = useState([0, 0]);
  const slide = SLIDES[i];
  const Icon = slide.icon;

  const paginate = (d) =>
    setState(([prev]) => [(prev + d + SLIDES.length) % SLIDES.length, d]);
  const goTo = (idx) => setState(([prev]) => [idx, idx >= prev ? 1 : -1]);

  const onDragEnd = (_, info) => {
    if (info.offset.x < -60) paginate(1);
    else if (info.offset.x > 60) paginate(-1);
  };

  return (
    <section data-testid="feature-slides" className="mx-auto max-w-7xl px-4 py-14 sm:px-6 sm:py-20">
      <div className="mb-6 sm:mb-8">
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-zinc-500">// How it works</p>
        <h2 className="mt-2 font-display text-3xl font-bold tracking-tight sm:text-4xl">The whole journey, one place.</h2>
      </div>

      <div className="relative overflow-hidden border border-white/10 bg-[#0A0A0B]">
        {/* Single navigation method: overlaid arrows (+ dots as jump/indicator, + swipe on touch) */}
        <button data-testid="slide-prev" onClick={() => paginate(-1)} aria-label="Previous slide"
          className="absolute left-2 top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-white/15 bg-black/60 text-zinc-100 backdrop-blur transition-colors hover:bg-primary hover:text-white sm:left-3 sm:h-11 sm:w-11">
          <ChevronLeft className="h-5 w-5" />
        </button>
        <button data-testid="slide-next" onClick={() => paginate(1)} aria-label="Next slide"
          className="absolute right-2 top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-white/15 bg-black/60 text-zinc-100 backdrop-blur transition-colors hover:bg-primary hover:text-white sm:right-3 sm:h-11 sm:w-11">
          <ChevronRight className="h-5 w-5" />
        </button>

        <AnimatePresence mode="wait" custom={dir}>
          <motion.div
            key={i}
            custom={dir}
            variants={variants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.35, ease: "easeInOut" }}
            drag="x"
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={0.2}
            onDragEnd={onDragEnd}
            className="flex min-h-[360px] cursor-grab flex-col justify-center gap-6 px-12 py-10 active:cursor-grabbing sm:min-h-[340px] sm:px-20 sm:py-14 md:grid md:grid-cols-2 md:items-center md:gap-8"
          >
            <div>
              <div className="flex h-11 w-11 items-center justify-center border border-primary/40 bg-primary/10 text-primary sm:h-12 sm:w-12">
                <Icon className="h-5 w-5 sm:h-6 sm:w-6" />
              </div>
              <p className="mt-5 font-mono text-xs uppercase tracking-[0.3em] text-primary sm:mt-6">{slide.kicker}</p>
              <h3 className="mt-3 font-display text-2xl font-bold sm:text-3xl">{slide.title}</h3>
              <p className="mt-3 max-w-md text-sm text-zinc-400 sm:mt-4 sm:text-base">{slide.body}</p>
            </div>
            <div className="hidden items-center justify-center md:flex">
              <div className="font-display text-[7rem] font-black leading-none text-white/5 sm:text-[10rem]">
                {String(i + 1).padStart(2, "0")}
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>

      <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
        {SLIDES.map((_, idx) => (
          <button key={idx} data-testid={`slide-dot-${idx}`} onClick={() => goTo(idx)} aria-label={`Go to slide ${idx + 1}`}
            className={`h-1.5 rounded-full transition-all ${idx === i ? "w-8 bg-primary" : "w-1.5 bg-white/20 hover:bg-white/40"}`} />
        ))}
      </div>
    </section>
  );
}
